import { Pattern } from './Pattern';
import { PatternType, Difficulty, Bounds, Vector2, DotState } from '../types';

/**
 * HunterKiller - A small number of "hunter" dots actively chase the player,
 * leaving stationary trail dots behind them. The hunters are faster than
 * zombie dots but slower than the player. Players must outmaneuver them while
 * the trail dots accumulate as obstacles.
 */
export class HunterKiller extends Pattern {
  readonly type = PatternType.HUNTER_KILLER;
  difficulty: Difficulty = Difficulty.MEDIUM;

  private readonly duration: number = 14000;
  private hunterCount: number = 3;
  private hunterSpeed: number = 95;
  private trailIntervalMs: number = 180;
  private timeSinceTrail: number = 0;
  private hasSpawned: boolean = false;
  private hunters: Array<{ dotIndex: number }> = [];

  constructor(difficulty: Difficulty = Difficulty.MEDIUM) {
    super();
    this.difficulty = difficulty;
    switch (difficulty) {
      case Difficulty.HARD:
        this.hunterCount = 4;
        this.hunterSpeed = 130;
        this.trailIntervalMs = 130;
        break;
      case Difficulty.MEDIUM:
        this.hunterCount = 3;
        this.hunterSpeed = 100;
        this.trailIntervalMs = 180;
        break;
      default:
        this.hunterCount = 2;
        this.hunterSpeed = 75;
        this.trailIntervalMs = 240;
    }
  }

  spawn(_center: Vector2, bounds: Bounds): void {
    this.start();
    // Spawn hunters around the edge
    for (let i = 0; i < this.hunterCount; i++) {
      const angle = (i / this.hunterCount) * Math.PI * 2;
      const dist = Math.min(bounds.width, bounds.height) * 0.4;
      const x = bounds.width / 2 + Math.cos(angle) * dist;
      const y = bounds.height / 2 + Math.sin(angle) * dist;
      const dot = this.spawnDot(x, y);
      dot.skipSpawnAnimation();
      dot.state = DotState.ACTIVE;
      // Mark this dot as a hunter (we'll track first N dots as hunters)
      this.hunters.push({ dotIndex: this.dots.length - 1 });
    }
    this.hasSpawned = true;
  }

  update(dt: number, playerPosition: Vector2, bounds: Bounds): void {
    // Drop trail dots periodically while spawning is allowed
    if (this.elapsedMs <= this.duration) {
      this.timeSinceTrail += dt * 1000;
      if (this.timeSinceTrail >= this.trailIntervalMs) {
        this.timeSinceTrail = 0;
        this.dropTrails();
      }
    }

    // Move hunters toward the player
    for (const h of this.hunters) {
      const dot = this.dots[h.dotIndex];
      if (!dot || dot.isDead() || dot.isFrozen()) continue;

      const pos = dot.getPosition();
      const dx = playerPosition.x - pos.x;
      const dy = playerPosition.y - pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0) {
        dot.velocity.x = (dx / dist) * this.hunterSpeed;
        dot.velocity.y = (dy / dist) * this.hunterSpeed;
      }
    }

    // Update all dots
    for (let i = this.dots.length - 1; i >= 0; i--) {
      const dot = this.dots[i];
      const pos = dot.getPosition();

      if (dot.isFrozen()) {
        dot.update(dt, bounds, playerPosition);
        continue;
      }

      if (pos.x < -100 || pos.x > bounds.width + 100 ||
          pos.y < -100 || pos.y > bounds.height + 100) {
        // Trail dots that wander offscreen get removed
        // (only trail dots, since hunters chase the player who is on-screen)
        const isHunter = this.hunters.some(h => h.dotIndex === i);
        if (!isHunter) {
          this.dots.splice(i, 1);
          continue;
        }
      }

      dot.update(dt, bounds, playerPosition);
    }
  }

  private dropTrails(): void {
    for (const h of this.hunters) {
      const hunter = this.dots[h.dotIndex];
      if (!hunter || hunter.isDead() || hunter.isFrozen()) continue;
      const pos = hunter.getPosition();
      // Spawn a stationary trail dot at the hunter's current position
      const trail = this.spawnDot(pos.x, pos.y, { x: 0, y: 0 });
      trail.skipSpawnAnimation();
    }
  }

  isComplete(): boolean {
    return this.hasSpawned && this.elapsedMs > this.duration && this.dots.length === 0;
  }
}
